/**
 * Who is signed in, and the two things they can do about it (docs/07-gui-proposal §2) — since
 * 12/09/2026 at the BOTTOM of the sidebar instead of the far right of the top bar
 * (docs/13-tasks/01-ui-chrome.md#18 §2), which is where every desktop tool of this shape puts it.
 *
 * The popup opens UPWARD (`.accountpop`): the trigger sits on the last row of the window, so a
 * menu dropping down would open off-screen.
 *
 * Collapsed to the rail there is room for the initials only; the name and the role then ride on
 * the trigger's `title`, and the popup — which is not collapsed — still names them in full.
 */
import { useNavigate } from 'react-router';

import { useSignOut } from '../hooks/useSession.js';
import type { SessionUser } from '../services/authApi.js';

const ROLE_LABELS: Readonly<Record<SessionUser['role'], string>> = {
  admin: 'Quản trị',
  viewer: 'Chỉ xem',
};

/**
 * Two letters for the avatar tile: the first letter of the first word and of the last. A single
 * word gives one letter rather than an empty tile.
 */
export function initialsOf(fullName: string): string {
  const words = fullName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0] ?? '';
  const last = words[words.length - 1] ?? '';
  return (first.charAt(0) + (words.length > 1 ? last.charAt(0) : '')).toUpperCase();
}

export function AccountMenu({ user, rail }: { user: SessionUser; rail: boolean }) {
  const navigate = useNavigate();
  const leave = useSignOut();
  const role = ROLE_LABELS[user.role];

  return (
    <details className="account">
      <summary className="who" title={rail ? `${user.fullName} · ${role}` : undefined}>
        <span className="avatar" aria-hidden="true">
          {initialsOf(user.fullName)}
        </span>
        <span className="whotext">
          <b>{user.fullName}</b>
          <span>{role}</span>
        </span>
      </summary>

      <div className="accountpop">
        <span className="accountid">
          {user.username} · {user.authMethod === 'google' ? 'Google' : 'nội bộ'}
        </span>
        {user.authMethod === 'local' && (
          <button
            type="button"
            className="accountact touch"
            onClick={() => {
              void navigate('/change-password');
            }}
          >
            Đổi mật khẩu
          </button>
        )}
        <button
          type="button"
          className="accountact touch"
          disabled={leave.isPending}
          onClick={() => {
            leave.mutate(undefined, {
              onSuccess: () => {
                void navigate('/login', { replace: true });
              },
            });
          }}
        >
          {leave.isPending ? 'Đang thoát…' : 'Đăng xuất'}
        </button>
      </div>
    </details>
  );
}
